import type { GetStaticPropsContext, NextPage } from 'next'
import { JobPostingJsonLd } from 'next-seo'
import { useRouter } from 'next/router'

import { Article } from '../../components/article'
import { Box } from '../../components/box'
import { Breadcrumbs } from '../../components/breadcrumbs'
import { GreenHouseForm } from '../../components/greenhouse/form'
import { Section } from '../../components/layout/section'
import { DEFAULT_JSON } from '../../contentful-components/page-meta/default-meta'
import type { PageFragment } from '../../graphql/fragments/PageFragment.graphql'
import { PersonalizationProvider } from '../../lib/hooks/usePersonalizations'
import { fetchContentfulPage } from '../../lib/static/fetchStaticPage'
import { PAGE_OPTIONS } from '../../lib/utils/page'
import { stripHTML, TEXT } from '../../lib/utils/text'
import { PageTemplate } from '../../page-templates/generic-page'
import { fetchDepartments, fetchJob } from '../../server/helpers/greenhouse'
import type { GreenHouseJob } from '../../server/helpers/greenhouse'
import { isPrd } from '../../shared/util/env'
import { LINK } from '../../shared/util/link'

/** This is configured in [https://app2.greenhouse.io/configure/dev_center/config](Greenhouse). */
const Careers: NextPage<Props> = ({ page, job }) => {
    const router = useRouter()

    const jobDescription = job?.content
        ? stripHTML(job.content).replace(/\n/g, ' ')
        : ''

    return (
        <PersonalizationProvider value={{ jobTitle: job?.title }}>
            {job && (
                <Section>
                    <Box pad={['horizontal', 'top']}>
                        <Breadcrumbs
                            current={job.title}
                            links={[
                                {
                                    text: TEXT.careers,
                                    href: LINK.careers().serialize(router),
                                },
                            ]}
                        />

                        <JobPostingJsonLd
                            title={job.title}
                            description={job.description || jobDescription}
                            jobLocation={DEFAULT_JSON.address}
                            datePosted={job.updatedAt}
                            validThrough=''
                            hiringOrganization={{
                                ...DEFAULT_JSON.organization,
                                sameAs: '',
                            }}
                        />
                    </Box>

                    <Box pad={['horizontal', 'top']}>
                        <Article title={job.title} subtitle={job.location.name}>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: job.content,
                                }}
                            />
                        </Article>
                    </Box>

                    <GreenHouseForm {...job} />
                </Section>
            )}

            <PageTemplate
                pageData={page}
                {...PAGE_OPTIONS.CAREER_JOB}
                meta={{
                    ...PAGE_OPTIONS.CAREER_JOB.meta,
                    canonical: job && LINK.job(job.id),
                }}
            />
        </PersonalizationProvider>
    )
}

interface Props {
    job: GreenHouseJob
    page: PageFragment
}

export const getStaticPaths = async () => {
    const departments = await fetchDepartments()

    const jobs = departments.map((dept) => dept.jobs).flat()

    const paths = jobs.map((job) => ({
        params: {
            id: `${job.id}`,
        },
    }))

    return {
        paths: isPrd() ? paths : [],
        fallback: 'blocking',
    }
}

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
    if (typeof params?.id !== 'string') return { notFound: true }

    const job = await fetchJob(params.id)

    if (!job) return { notFound: true }

    const page = await fetchContentfulPage(PAGE_OPTIONS.CAREER_JOB.slug)

    return {
        props: { job, page },
    }
}

export default Careers
